import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';

import { PersonReadModelSort, SortOrder } from '@client/api';
import { DropDownSort, DropDownSortEntry, SearchField, useSearching, useSorting } from '@client/toolkit';

import { useUserListSearchQueryParams, useUserListSortQueryParams } from '../utils/use-user-list-query-params';

const SORT_OPTIONS: DropDownSortEntry<PersonReadModelSort, SortOrder>[] = [
  { property: 'FirstName', order: 'Asc', label: 'user.firstName' },
  { property: 'FirstName', order: 'Desc', label: 'user.firstName' },
  { property: 'LastName', order: 'Asc', label: 'user.lastName' },
  { property: 'LastName', order: 'Desc', label: 'user.lastName' },
  { property: 'Email', order: 'Asc', label: 'user.email' },
  { property: 'Email', order: 'Desc', label: 'user.email' },
];

interface UsersToolbarProps {
  totalCount?: number;
}

export const UsersToolbar = ({ totalCount }: UsersToolbarProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [searchQueryParams, setSearchQueryParam] = useUserListSearchQueryParams();
  const [sortQueryParams, setSortQueryParam] = useUserListSortQueryParams();
  const { search, clearSearch, debouncedSearch, updateSearch } = useSearching({
    search: searchQueryParams.usersSearch,
  });
  const { sortProperty, sortOrder, updateSortOrder, updateSortProperty } = useSorting<PersonReadModelSort, SortOrder>({
    sortProperty: sortQueryParams.usersSortProperty ?? 'FirstName',
    sortOrder: sortQueryParams.usersSortOrder ?? 'Asc',
  });

  useUpdateEffect(() => {
    setSearchQueryParam({
      usersSearch: debouncedSearch ?? undefined,
    });
  }, [debouncedSearch]);

  useUpdateEffect(() => {
    setSortQueryParam({
      usersSortOrder: sortOrder ?? undefined,
    });
  }, [sortOrder]);

  useUpdateEffect(() => {
    setSortQueryParam({
      usersSortProperty: sortProperty ?? undefined,
    });
  }, [sortProperty]);

  return (
    <div className="flex justify-between items-center py-4">
      <div className="flex w-6/12 items-center">
        <SearchField value={search} onChange={updateSearch} onClear={clearSearch} placeholder={tCommon('search')} />
        <div className="flex">
          <span className="px-2 whitespace-nowrap text-white">{`${totalCount} ${tGlobal('entity.user', {
            count: totalCount ?? 0,
            context: totalCount && totalCount > 1 ? 'plural' : 'singular',
          })}`}</span>
        </div>
      </div>
      <DropDownSort<PersonReadModelSort, SortOrder>
        selected={{
          order: sortOrder,
          property: sortProperty,
        }}
        onSortChange={(entry) => {
          updateSortOrder(entry.order);
          updateSortProperty(entry.property);
        }}
        promptLabel={`${tCommon('sortBy')}:`}
        getSelectedLabel={(label) => (label ? tGlobal(label) : '')}
        getOptionLabel={(label) => (label ? tGlobal(label) : '')}
        ascLabel={tCommon('asc')}
        descLabel={tCommon('desc')}
        columns={SORT_OPTIONS}
      />
    </div>
  );
};
